/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { AreaService } from '@src/app/modules/unit/area.service';
import { of } from 'rxjs';
import { Area } from '@src/app/model/area';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { required, uniqueGrid } from '@src/app/shared/validators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { Field } from '@src/app/shared/field/Field';
import { Core } from '@src/app/model/core';
import { GridField } from '@src/app/shared/grid/grid-field';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { AreaPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-area-page',
    templateUrl: './area-page.component.html',
    styleUrls: ['./area-page.component.scss'],
    animations: [STD_ANIMATION],
    imports: [FormPageComponent]
})

export class AreaPageComponent extends AbstractPageComponent {
  dataSvc = inject(AreaService);
  dialogRef = inject<MatDialogRef<AreaPageComponent>>(MatDialogRef, { optional: true });
  dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


  unitsLink = FormNavLinkComponent.make('Units', UnitPageNavRoute, o => ({ areaId: o.id }),
      { formColumn: 3, value: 'units_count', cellOpts: { style: 'text-align: center' } }
  );

  ownerOccupiedLink = FormNavLinkComponent.make('Owner Occupied', UnitPageNavRoute,
    o => ({ areaId: o.id, ownerOccupied: 1 }),
      { formColumn: 3, value: 'owner_occupied_count', cellOpts: { style: 'text-align: center' } }
  );

  nonOwnerOccupiedLink = FormNavLinkComponent.make('Non Owner Occupied', UnitPageNavRoute,
    o => ({ areaId: o.id, ownerOccupied: 0  }),
      { formColumn: 3, value: 'non_owner_occupied_count', cellOpts: { style: 'text-align: center' } }
  );

  coreCountField = FormNumberComponent.make('Cores', 'cores_count', {format: 'plain'}, {
    readonly: true, sendServer: false, visible: Field.noForm, cellOpts: {style: 'text-align: center'}
  });

  coresGrid =  new GridField({
        field: { value: 'cores', cellOpts: { heading: 'Cores' }, visible: Field.formOnly, formColumn:2 },
        rowFactory: () => [
            FieldMaker.id(),
            FieldMaker.nameControl({ validators: [required], cellOpts: { heading: 'Cores'}}),
            FormNavLinkComponent.make('Units', UnitPageNavRoute, o => ({ coreId: o.id }),
              { value: 'unit_count', cellOpts: { style: 'text-align: center' } }
            ),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow(),
        ],
        objFactory: () => of(new Core()),
        gridValidator: [uniqueGrid()]
    })

  config = new FormConfig({
    navRoute: AreaPageNavRoute,
    docPath: 'omc-community/managers/areas',
    title: $localize`Areas`,
    help: $localize`The different areas within a development units are in or that service requests might relate to.`,
    fieldSet: new FieldSet({
      fields: [
        FieldMaker.id(),
        FieldMaker.rev(),
        FieldMaker.nameControl({ validators: [required] }),
        this.coresGrid,
        this.unitsLink,
        this.coreCountField,
        this.ownerOccupiedLink,
        this.nonOwnerOccupiedLink,
        FieldMaker.notes(),
      ],
      formLayout: LAYOUT_OPTIONS.threeCol,
    }),
    service: this.dataSvc,
    mode: 'list',
    objectFactory: this.newArea.bind(this),
  });
  newArea() {
    const o = new Area();
    if (this.dialogOptions) {
        o.name = this.dialogOptions['name'];
    }
    return of(o);
}
}
